/**
 * Global light/dark theme — single source of truth for the whole app.
 *
 * The theme is persisted in the same localStorage record the dashboard already
 * uses (see @/utils/dashboardLayout) so there is exactly one stored value and
 * the dashboard's existing toggle keeps working. Flipping the theme sets
 * Vuetify's global `$vuetify.theme.dark`, which re-themes every Vuetify
 * component (cards, tables, inputs, dialogs, menus, navbars, ...) at once.
 *
 * Usage:
 *   import { isDark, setDark, toggleTheme } from '@/utils/theme'
 *   toggleTheme(this.$vuetify)            // flip + persist + apply
 *   setDark(this.$vuetify, true)          // force dark
 *   applyTheme(this.$vuetify)             // apply the saved value (e.g. on app boot)
 */
import { loadLayout, saveLayout } from '@/utils/dashboardLayout'

export function isDark () {
  return loadLayout().theme === 'dark'
}

/** Persist the theme into the shared dashboard-layout record. */
function persist (dark) {
  const current = loadLayout()
  saveLayout({ ...current, theme: dark ? 'dark' : 'light' })
}

/** Apply the given (or saved) value to Vuetify + persist it. */
export function setDark (vuetify, dark) {
  const value = !!dark
  if (vuetify && vuetify.theme) vuetify.theme.dark = value
  // mirror onto <html> so plain CSS (non-Vuetify markup) can react too
  if (typeof document !== 'undefined') {
    document.documentElement.classList.toggle('app-dark', value)
  }
  persist(value)
  return value
}

/** Apply the saved theme to Vuetify without changing it (call on app boot). */
export function applyTheme (vuetify) {
  return setDark(vuetify, isDark())
}

/** Flip light <-> dark, persist, and return the new value. */
export function toggleTheme (vuetify) {
  return setDark(vuetify, !isDark())
}
